import { DefaultModal } from 'components/Modals/DefaultModal';
import { useInviteTeamMembersModalStyles } from 'components/Modals/InviteTeamMembersModal/styles';
import { Card } from 'components/TeamOverview/components/Common/Card/Card';
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import { Controller, useForm } from 'react-hook-form';
import React, { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import { yupResolver } from '@hookform/resolvers/yup';
import { InvitedMembersTable } from 'components/Modals/InviteTeamMembersModal/InvitedMembersTable';
import { EMAIL_ALREADY_SENT_MESSAGE } from 'components/Modals/InviteTeamMembersModal/constants';
import { validationSchema } from 'components/Modals/InviteTeamMembersModal/utilities';
import { useInvites } from 'components/Modals/InviteTeamMembersModal/hooks';
import { useGetTeamsQuery } from 'store/apis/teamsApi';
import { useQueryError } from 'utilities/hooks/useQueryError/useQueryError';
import { FETCH_TEAMS_ERROR_MESSAGE } from 'constants/errorMessages';

const InviteTeamMembersModalHeader = ({ classes, handleClose }) => (
  <Box className={classes.header}>
    <Box className={classes.row}>
      <PersonAddAlt1OutlinedIcon className={classes.personIcon} />
      <Typography className={classes.modalLabel}>
        INVITE NEW USER TO TEAM
      </Typography>
    </Box>
    <ClearOutlinedIcon onClick={handleClose} cursor="pointer" />
  </Box>
);

export const InviteTeamMembersModal = ({ open, handleClose }) => {
  const { classes } = useInviteTeamMembersModalStyles();

  const [selectedTeam, setSelectedTeam] = useState(null);
  const { showWarning } = useSnackbar();
  const { invitedUsers, postInvite } = useInvites();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isValid },
  } = useForm({ mode: 'all', resolver: yupResolver(validationSchema) });

  const handleTeamSelection = useCallback(
    (team) => {
      setSelectedTeam(team);
      setValue('teamUUID', team?.uuid);
    },
    [setSelectedTeam, setValue],
  );

  const { data: teams, error } = useGetTeamsQuery();
  useQueryError(error, FETCH_TEAMS_ERROR_MESSAGE);

  useEffect(() => {
    if (teams?.length > 0) {
      handleTeamSelection(teams[0]);
    }
  }, [teams, handleTeamSelection]);

  const onSubmit = (values) => {
    if (invitedUsers.some(({ email }) => email === values.email)) {
      showWarning(EMAIL_ALREADY_SENT_MESSAGE);

      return;
    }

    postInvite(values);
  };

  return (
    <DefaultModal
      open={!!open}
      styles={{
        width: '1000px',
        padding: 0,
      }}
    >
      <Card
        header={
          <InviteTeamMembersModalHeader
            classes={classes}
            handleClose={handleClose}
          />
        }
      >
        <Box className={classes.cardBody}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box className={classes.emailInput}>
              <TextField
                className={classes.inputForm}
                error={!!errors.email?.message}
                label="Email ..."
                {...register('email')}
                type="email"
              />
              {errors.email?.message && (
                <span className={classes.errorMessage} role="alert">
                  {errors.email.message}
                </span>
              )}
            </Box>
            <Controller
              control={control}
              name="teamUUID"
              render={({ field: { onBlur, ref } }) => (
                <Autocomplete
                  disableClearable
                  options={teams}
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputRef={ref}
                      label="Teams"
                      margin="normal"
                    />
                  )}
                  onBlur={onBlur}
                  className={classes.inputForm}
                  value={selectedTeam}
                  onChange={(e, value) => {
                    handleTeamSelection(value);
                  }}
                />
              )}
            />

            <Button
              variant="contained"
              color="primary"
              className={classes.buttonStyle}
              size="large"
              type="submit"
              disabled={!isValid}
            >
              Invite
            </Button>
          </form>
          {!!invitedUsers.length && (
            <Box className={classes.inviteTable}>
              <InvitedMembersTable invitedUsers={invitedUsers} />
            </Box>
          )}
        </Box>
      </Card>
    </DefaultModal>
  );
};
